import React, { Fragment, useState } from 'react';
import Banner from '../UI/Banner';
import { Background, Main } from '../../styles/themeColors';
import FlexboxContainer from '../UI/FlexboxContainer';
import * as Cons from 'data/Consignment';
import Button from '../UI/Button';
import { AFFILIATE_PROGRAM_ACTIVE, CANCELED, CANCELING } from '../../constants/consignmentStates';
import { useAPI } from '../../lib/API';
import { cancelConsignment } from '../../requests/consignment';
import { renderToast, toast } from '../UI/ToastNotification';
import { TEXT_LABELS } from 'constants/order';
import CancellationReasonModal from './Modals/CancellationReasonModal';
import AffiliateFormSubmitted from './AffiliateFormSubmitted';

interface AffiliateOrderStatesProps {
  consignmentData?: Cons.ConsignmentDetails;
  documents?: any;
  refreshConsignment?: () => void;
};

export const AffiliateActive = ({ consignmentData, documents, refreshConsignment }: AffiliateOrderStatesProps) => {
  const [showDeactivationModal, setShowDeactivationModal] = useState(false);

  const deactivateReq = useAPI({
    deferFn: cancelConsignment,
    onResolve: () => {
      renderToast(toast.TYPE.SUCCESS, TEXT_LABELS.ORDER_UPDATED_SUCCESSFULLY);
      setShowDeactivationModal(false);
      refreshConsignment?.();
    },
    onReject: (err: any) => {
      renderToast(toast.TYPE.ERROR, err.message);
    }
  });

  return (
    <Fragment>
      <Banner
        boxShadow='0px 8px 16px rgba(47, 66, 83, 0.25), 0px 16px 20px rgba(47, 66, 83, 0.15)'
        bgColor={Background.white}
        titleColor={Main.black}
        title={TEXT_LABELS.AFFILIATE_PROGRAM_DETAILS}
      >
        <div style={{marginTop: 10}}>
          This Affiliate Program is active.<br />
          Affiliate Members can generate links and earn commission on eligible sales. <br />
          Manage payouts, approvals, and program activity from the Affiliates section in Seller Hub.
        </div>
        <div style={{marginTop: 16}}>
          <AffiliateFormSubmitted
            documents={documents}
            isAffiliateFormPdfGenerated={consignmentData?.isAffiliateFormPdfGenerated}
            submittedOn={consignmentData?.affiliateFormSubmittedOn}
            isVisible={consignmentData?.hasCognitoForm}
          />
        </div>
        <FlexboxContainer
              margin="25px 0px"
              justifyContent='flex-end'
            >
              <FlexboxContainer
                justifyContent="flex-end"
                alignItems="flex-end"
                margin='0 4px 0 0'
              >
                <Button
                  bgColor={Main.error2}
                  borderColor={Main.error2}
                  onClick={() => setShowDeactivationModal(true)}
                >
                  {TEXT_LABELS.DEACTIVATE_AFFILIATE}
                </Button>
              </FlexboxContainer>
            </FlexboxContainer>
      </Banner>

      <CancellationReasonModal
        show={showDeactivationModal}
        onClose={() => setShowDeactivationModal(false)}
        onConfirm={(reason) => deactivateReq.run(consignmentData?.consignmentId, reason)}
        isLoading={deactivateReq.isLoading}
        onSave={refreshConsignment}
        title={TEXT_LABELS.DEACTIVATE_AFFILIATE_TITLE}
        warningText={TEXT_LABELS.DEACTIVATE_AFFILIATE_WARNING}
        reasonLabel={TEXT_LABELS.DEACTIVATE_AFFILIATE_REASON}
        confirmButtonText={TEXT_LABELS.DEACTIVATE_AFFILIATE_CONFIRM}
        ghostButtonText='Cancel'
      />
    </Fragment>
  )
}

const AffiliateOrderStates = ({ consignmentData, documents, refreshConsignment }: AffiliateOrderStatesProps) => {
  const state = consignmentData?.state || '';

  const renderStateComponent = () => {
    switch (state) {
      case AFFILIATE_PROGRAM_ACTIVE:
        return <AffiliateActive consignmentData={consignmentData} documents={documents} refreshConsignment={refreshConsignment} />;
      case CANCELED:
      case CANCELING:
        return (
          <AffiliateFormSubmitted
            documents={documents}
            isAffiliateFormPdfGenerated={consignmentData?.isAffiliateFormPdfGenerated}
            submittedOn={consignmentData?.affiliateFormSubmittedOn}
            isVisible={consignmentData?.hasCognitoForm}
          />
        );
      default:
        return <div></div>;
    }
  };

  return <Fragment>{renderStateComponent()}</Fragment>;
};

export default React.memo(AffiliateOrderStates);
